import React from "react";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { registerUser } from "@/store/slices/authSlice";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "@/hooks/useAuth"; // Using custom hook
import Lottie from "lottie-react";
import cyclingAnimation from "@/assets/icons/Cycling-json.json";
import DatePicker from "../common/DatePicker";
import ParticleBackground from "../home/ParticleBackground";
import Button from "../common/Button";
import TextInput from "../common/TextInput";

const RegisterForm = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, clearErrors } = useAuth(); // Using custom hook

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm();

  const password = watch("password");

  // Clear errors when component mounts
  React.useEffect(() => {
    clearErrors();
  }, []);

  const onSubmit = async (data) => {
    try {
      await dispatch(
        registerUser({
          ...data,
          username: data.username.toLowerCase(),
        })
      ).unwrap();
      toast.success("Registration successful!");
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Dynamic Background */}
      <ParticleBackground />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 transition-colors duration-300"
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24  flex items-center justify-center mx-auto mb-4">
            <Lottie
              animationData={cyclingAnimation}
              loop={true}
              autoplay={true}
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors duration-300">
            Join Koset
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors duration-300">
            Create your cycling account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Full Name */}
          <TextInput
            label="Full Name"
            icon={<User className="w-5 h-5" />}
            placeholder="Enter your full name"
            error={errors.name?.message}
            {...register("name", {
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters",
              },
            })}
          />

          {/* Username */}
          <div>
            <TextInput
              label="Username"
              icon={<User className="w-5 h-5" />}
              placeholder="Choose a username"
              error={errors.username?.message}
              {...register("username", {
                required: "Username is required",
                pattern: {
                  value: /^[a-z0-9_]{3,30}$/,
                  message:
                    "3–30 chars, lowercase letters, numbers, and underscores only",
                },
                onChange: (e) => {
                  e.target.value = e.target.value.toLowerCase();
                },
              })}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 mb-3 px-1 transition-colors duration-300">
              Allowed: lowercase letters, numbers, underscores (3–30 characters)
            </p>
          </div>

          {/* Date of Birth */}
          <div>
            <Controller
              control={control}
              name="date_of_birth"
              rules={{ required: "Date of Birth is required" }}
              render={({ field }) => (
                <DatePicker
                  label="Date of Birth"
                  value={field.value}
                  onChange={field.onChange}
                  id="date_of_birth"
                  allowFuture={false}
                  className="w-full"
                />
              )}
            />
            {errors.date_of_birth && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1"
              >
                {errors.date_of_birth.message}
              </motion.p>
            )}
          </div>

          {/* Email */}
          <TextInput
            label="Email Address"
            type="email"
            icon={<Mail className="w-5 h-5" />}
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Invalid email address",
              },
            })}
          />

          {/* Password (with toggle) */}
          <div className="relative">
            <TextInput
              label="Password"
              type={showPassword ? "text" : "password"}
              icon={<Lock className="w-5 h-5" />}
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Confirm Password (with toggle) */}
          <div className="relative">
            <TextInput
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              icon={<Lock className="w-5 h-5" />}
              placeholder="Confirm your password"
              error={errors.confirmPassword?.message}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <Button
            type="submit"
            loading={isSubmitting}
            variant="primary"
            className="w-full py-3"
            size="lg"
          >
            Sign Up
          </Button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-semibold transition-colors duration-300"
            >
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterForm;
